import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DataTable, { Row } from "components/DataTable";
import apiService from "services/apiService";
import { FaEdit, FaEye, FaRegHospital } from "react-icons/fa";
import Modal from "components/Modal";
import EmployeeDetails from "./EmployeeDetails";
import AddEmployee from "./AddEmployee";
import EditEmployee from "./EditEmployee";
import { useTranslation } from "react-i18next";
import GenericToaster from "components/Toaster";
import { Link, useSearchParams } from "react-router-dom";
import Export from "components/Export";
import EmployeeRosterForm from "./employee-roster/EmployeeRosterForm";
import DeviceSettings from "pages/tracking/settings/DeviceSettings";
import { colors } from "themes";
import { Gender } from "../../util/types";
import { getImageUrl } from "util/googleDriveHelper";
import { BannerContext } from "components/SideBar";
import { TruncatedText } from "pages/AdminConfig/components/TruncatedText";
import CommonListingFilter, {
  CommonFilterField,
} from "components/CommonListingFilter/CommonListingFilter";


interface ProjecProp {
  projectId: number;
  name: string;
  manager: string;
  teamSize: string;
  startDate: string;
  completionDate: string;
  clientContactName: string;
  clientContactNumber: string;
  projectDetail: string;
  emails: string;
  projectAssignedDate: string;
}

/**
 * Project name with an on-demand tooltip.
 *
 * The name truncates with an ellipsis inside its flex cell. A tooltip is shown
 * on hover ONLY when the text is actually clipped (scrollWidth > clientWidth),
 * so short names don't get a redundant tooltip. The tooltip is rendered through
 * a portal to <body> so it is never clipped by the scrollable project list or
 * the modal — a plain absolutely-positioned tooltip would be cut off at those
 * overflow boundaries.
 */
const ProjectName = ({ name }: { name: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);

  const showTip = () => {
    const el = textRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return; // not truncated → no tooltip
    const rect = el.getBoundingClientRect();
    setTip({ x: rect.left + rect.width / 2, y: rect.top });
  };
  const hideTip = () => setTip(null);

  return (
    <span
      className="min-w-0 flex-1"
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
    >
      <span
        ref={textRef}
        className="block truncate text-sm font-medium text-slate-700 dark:text-slate-200"
      >
        {name}
      </span>

      {tip &&
        createPortal(
          <span
            style={{
              position: "fixed",
              left: tip.x,
              top: tip.y - 8,
              transform: "translate(-50%, -100%)",
            }}
            className="z-[9999] pointer-events-none max-w-xs whitespace-normal break-words rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg"
          >
            {name}
          </span>,
          document.body,
        )}
    </span>
  );
};

interface ResponseProps {
  projectNames: string[];
  assetModels: string[];
}

interface EmployeeData {
  id: number;
  displayName: string;
  email: string;
  mobileNumber: string;
  gender: Gender;
  [key: string]: unknown;
}

export const AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-orange-500",
  "bg-pink-500", "bg-teal-500", "bg-indigo-500", "bg-rose-500",
];

// Valid values for the employee `status` filter. Casing is intentional and must
// match the API's `status` query param exactly ("ONBOARDING" is upper-case).
const EMPLOYEE_STATUS_FILTERS = ["active", "inactive", "ONBOARDING"] as const;
const DEFAULT_EMPLOYEE_STATUS = "active";

// Map an incoming ?status= value (e.g. from an ONBOARDING_REVIEW_PENDING
// notification deep-link) to its canonical filter value, case-insensitively.
// Returns null when the param is absent or unrecognised.
const resolveStatusParam = (param: string | null): string | null => {
  if (!param) return null;
  return (
    EMPLOYEE_STATUS_FILTERS.find((v) => v.toLowerCase() === param.toLowerCase()) ?? null
  );
};

// Shows the employee's real profile photo; falls back to a coloured initials circle
const Avatar = ({ name, imageUrl }: { name: string; imageUrl?: string }) => {
  const [imgError, setImgError] = useState(false);
  const str = name || "?";
  const color = AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];
  const initials = str.split("@")[0].substring(0, 2).toUpperCase();

  if (imageUrl && !imgError) {
    return (
      <img
        src={getImageUrl(imageUrl)}
        alt={name}
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
        className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-white dark:border-gray-700 shadow-sm"
      />
    );
  }

  return (
    <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 border-2 border-white dark:border-gray-700 shadow-sm`}>
      {initials}
    </div>
  );
};
function EmployeeList() {
  const NAME_TRUNCATION_WIDTH_MULTIPLIER = 1.7;

  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  // const [openModal, setOpenModal] = useState(false);
  const [viewData, setViewData] = useState<Row | undefined>();
  const [addEmployee, setAddEmployee] = useState(false);
  const [editEmployee, setEditEmployee] = useState(false);
  const [refresh, setRefresh] = useState(0);
  // Employee status filter. The API accepts these exact status values:
  // "active" | "inactive" | "ONBOARDING". `selectedStatus` is the applied value
  // that drives the request; `draftStatus` is the pending value inside the
  // filter panel until the user hits Apply. Both seed from a `?status=` deep-link
  // (e.g. the ONBOARDING_REVIEW_PENDING notification) so the right list loads on
  // the first render without an extra "active" fetch.
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const initialStatus = resolveStatusParam(statusParam) ?? DEFAULT_EMPLOYEE_STATUS;
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [draftStatus, setDraftStatus] = useState(initialStatus);
  const [showSuccessToaster, setSuccessToaster] = useState(false);
  const [showErrorToaster, setErrorToaster] = useState(false);
  const [message, setMessage] = useState("");
  const [changeUserStatus, setChangeUserStatus] = useState(false);
  const [currentStatusValue, setCurrentStatusValue] = useState<Row | null>(null);
  const [isCollectAssets, setIsCollectAssets] = useState(false);
  // True while the deactivation preview PUT is in flight (guards against
  // double-clicks and shows a busy state on the confirm button).
  const [isStatusPreviewLoading, setIsStatusPreviewLoading] = useState(false);
  const [isClickProject, setIsClickProject] = useState(false);
  const [projects, setProjects] = useState<ProjecProp[]>([]);
  // Deactivation-preview response: either { projectNames, assetModels } (Case 1)
  // or the "No projects or assets found for the user." string (Case 2).
  const [isResponse, setIsResponse] = useState<ResponseProps | string | null>(null);
  const [isView, setIsView] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isRosterAssigned, setIsRosterAssigned] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [nameMaxWidth, setNameMaxWidth] = useState<number | null>(null);
  const [emailMaxWidth, setEmailMaxWidth] = useState<number | null>(null);
  const nameReferenceRef = useRef<HTMLSpanElement | null>(null);
  const emailReferenceRef = useRef<HTMLSpanElement | null>(null);

  const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-110px)] min-h-[300px]"
    : "h-[calc(100vh-80px)] min-h-[300px]";

  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (nameReferenceRef.current && nameMaxWidth === null) {
      setNameMaxWidth(
        Math.ceil(
          nameReferenceRef.current.getBoundingClientRect().width *
          NAME_TRUNCATION_WIDTH_MULTIPLIER
        )
      );
    }

    if (emailReferenceRef.current && emailMaxWidth === null) {
      setEmailMaxWidth(Math.ceil(emailReferenceRef.current.getBoundingClientRect().width));
    }
  }, [emailMaxWidth, nameMaxWidth]);

  const getUsersFromPayload = (apiData: unknown): Row[] => {
    if (Array.isArray(apiData)) {
      return apiData as Row[];
    }

    if (
      typeof apiData === "object" &&
      apiData !== null &&
      "content" in apiData &&
      Array.isArray((apiData as { content?: unknown }).content)
    ) {
      return (apiData as { content: Row[] }).content;
    }

    return [];
  };

  useEffect(() => {
    setLoading(true);
    // Status is now driven by the applied filter (active / inactive / ONBOARDING).
    const params = new URLSearchParams();
    params.set("status", selectedStatus);
    params.set("page", String(currentPage));
    params.set("size", String(itemsPerPage));
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    }

    apiService
      .get(`api/users?${params.toString()}`)
      .then((res) => {
        const apiData = res.data;
        const listData: Row[] = getUsersFromPayload(apiData);
        const newData = [...listData].sort((a: Row, b: Row) =>
          (a.displayName as string).localeCompare(b.displayName as string),
        );
        setData(newData);
        const resolvedTotalItems =
          typeof apiData?.totalElements === "number"
            ? apiData.totalElements
            : listData.length;
        setTotalItems(resolvedTotalItems);
      })
      .catch((err) => {
        console.log("err", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refresh, selectedStatus, currentPage, itemsPerPage, searchQuery]);

  // Apply a `?status=` deep-link (e.g. clicking the ONBOARDING_REVIEW_PENDING
  // notification) even when the Employees page is already mounted. Seeding above
  // handles the first render; this handles arriving via an in-app navigation.
  useEffect(() => {
    const resolved = resolveStatusParam(statusParam);
    if (!resolved) return;
    setSelectedStatus(resolved);
    setDraftStatus(resolved);
    setCurrentPage(0);
  }, [statusParam]);

  // Directly set a user's status to ACTIVE (used when activating from INACTIVE or
  // ONBOARDING). No confirmation / asset-collection flow — that only applies when
  // DEACTIVATING an active user.
  const activateUser = async (value: Row) => {
    const updatedUser = { ...value, userStatus: "ACTIVE" };
    await apiService
      .put("api/user", updatedUser)
      .then((res) => {
        if (res.status === 200) {
          handleSuccess("Employee activated successfully");
          setRefresh((previous) => previous + 1);
        } else {
          handleError("Something went wrong while updating status");
        }
      })
      .catch((error) => {
        console.error(error);
        handleError(error?.response?.data?.message || "Something went wrong");
      });
  };

  const handleStatusClick = (value: Row) => {
    // The confirmation + asset-collection pop-up is only for ACTIVE → INACTIVE
    // (deactivation removes the user from projects and reclaims assets). For any
    // other transition (INACTIVE → ACTIVE, ONBOARDING → ACTIVE) just activate
    // directly without the pop-up.
    const isDeactivating =
      String(value.userStatus || "").toUpperCase() === "ACTIVE";
    if (isDeactivating) {
      setChangeUserStatus(true);
      setCurrentStatusValue(value);
    } else {
      void activateUser(value);
    }
  };
  // This Function is Updating UserStatus From ACTIVE to INACTIVE And Vice-Versa

  // Deactivation PREVIEW: PUT the status change and store the response (asset/
  // project info OR the "No projects or assets…" string). Intentionally shows NO
  // success toast — nothing is confirmed to the user until the result popup is
  // acknowledged. Returns the response data, or null on failure.
  const userStatusteHandle = async (store: string) => {
    if (!currentStatusValue) {
      return null;
    }
    const updatedStatus =
      currentStatusValue.userStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const updatedUser = { ...currentStatusValue, userStatus: updatedStatus };
    try {
      const res = await apiService.put(store, updatedUser);
      setIsResponse(res.data);
      return res.data;
    } catch (error) {
      console.error(error);
      handleError("Something went wrong while updating status");
      return null;
    }
  };
  // ------------------------------------------------

  const userShouldTrue = async (store: string) => {
    if (!currentStatusValue) {
      return;
    }
    console.log(currentStatusValue.userStatus);
    const updatedStatus =
      currentStatusValue.userStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const updatedUser = { ...currentStatusValue, userStatus: updatedStatus };
    console.log(updatedStatus);
    await apiService
      .put(store, updatedUser)
      .then((res) => {
        console.log("response >>>>>> ::::" + res);
        if (res.status === 200) {
          handleSuccess("Employee Updated successFully");
          setRefresh((previous) => previous + 1);
        } else {
          handleError("Something went Wrong While Updating status");
        }
      })
      .catch((error) => {
        console.error(error);
        handleError("Something is wrong" + error);
      })
      .finally(() => {
        setChangeUserStatus(false);
        setCurrentStatusValue(null);
        // setIsCollectAssets(false)
      });
  };

  //sonar fixes
  const getRoleBadge = (role: number) => {
    const roleConfig: Record<number, { label: string; className: string }> = {
      3: {
        label: t("employees.Sys_Admin"),
        className:
          "bg-success/10 text-success dark:bg-success/20 dark:text-success-400 border-success/20",
      },
      2: {
        label: t("employees.admin"),
        className:
          "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-400 border-primary/20",
      },
      4: {
        label: t("employees.manager"),
        className:
          "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-400 border-primary/20",
      },
    };

    const currentRole = roleConfig[role] || {
      label: t("employees.user"),
      className:
        "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-400 border-primary/20",
    };

    return (
      <span
        className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded border ${currentRole.className}`}
      >
        {currentRole.label}
      </span>
    );
  };

  const getStatusBadge = (row: string, value: Row) => {
    const isActive = row === "ACTIVE";

    return (
      <button
        onClick={() => handleStatusClick(value)}
        className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded border ${isActive
          ? "bg-success/10 text-success dark:bg-success/20 dark:text-success-400 border-success/20"
          : "bg-error/10 text-error dark:bg-error/20 dark:text-error-400 border-error/20"
          }`}
      >
        {row}
      </button>
    );
  };

  const renderProjectButton = (row: ProjecProp[] | undefined | null) => {
    const hasProjects = row && row.length > 0;

    return (
      <button
        className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded border ${hasProjects
          ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-400 border-primary/20"
          : "bg-error/10 text-error dark:bg-error/20 dark:text-error-400 border-error/20"
          }`}
        onClick={() => {
          setIsClickProject(true);
          if (hasProjects) {
            setProjects(row);
          }
        }}
      >
        {hasProjects ? row.length : 0}
      </button>
    );
  };
  //
  // -----------------------------------
  const tableColumns = [
    // { key: "id", label: `Id`, bold: true },
    // {
    //   key: "displayName",
    //   label: `${t("employees.name")}`,
    //   bold: true,
    //   render: (value: React.ReactNode) => {
    //     const text =
    //       typeof value === "string" || typeof value === "number"
    //         ? String(value)
    //         : "";

    //     return (
    //       <span
    //         className="block truncate"
    //         style={{ maxWidth: nameMaxWidth ? `${nameMaxWidth}px` : undefined }}
    //         title={text}
    //       >
    //         {text}
    //       </span>
    //     );
    //   },
    // },
    {
      key: "displayName",
      label: "Employee",
      render: (_value, row) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const r = row as any;
        const name = r.displayName || String(r.email || "").split("@")[0] || "—";
        const email = String(r.email || "");

        return (
          <div className="flex items-center gap-3">
            <Avatar name={name} imageUrl={r.imageUrl} />

            {/* The min-w-0 is critical here so flexbox allows shrinking below content size */}
            <div
              className="flex flex-col min-w-0"
              style={{ maxWidth: nameMaxWidth ? `${nameMaxWidth}px` : '200px' }}
            >
              <TruncatedText
                text={name}
                className="w-[200px] sm:w-[250px] lg:w-[350px] text-sm font-semibold text-gray-900 dark:text-white leading-tight"
              />

              {email && (
                <TruncatedText
                  text={email}
                  className="w-[200px] sm:w-[250px] lg:w-[350px] text-xs text-gray-400 dark:text-gray-500 leading-tight mt-0.5"
                />
              )}
            </div>
          </div>
        );
      },
    },

    {
      key: "mobileNumber",
      label: `${t("employees.mobileNumber")}`,
      bold: true,
    },
    {
      key: "userProjectWithAssignedDate",
      label: `Projects`,
      excludeExport: true,
      // eslint-disable-next-line
      render: (row: unknown) =>
        renderProjectButton(row as ProjecProp[]),
    },
    {
      key: "role",
      label: `${t("employees.role")}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (row: number) => getRoleBadge(row),
    },
    {
      key: "userStatus",
      label: `${t("employees.userStatus")}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (row: string, value: Row) => getStatusBadge(row, value),
    },
    {
      key: "actions",
      label: `${t("employees.actions")}`,
      excludeExport: true,
      actions: (row: object) => {
        const employee = row as Row;
        const isActive = String(employee.userStatus || "").toUpperCase() === "ACTIVE";

        return (
          <div className="flex flex-start">
            <span
              onClick={() => handleEdit(row)}
              className="btn-primary px-2 py-2 mr-2 rounded-lg"
            >
              <FaEdit />
            </span>
            <span
              onClick={() => handleView(row)}
              className="btn-primary px-2 py-2 mr-2 rounded-lg"
            >
              <FaEye />
            </span>
            {isActive && (
              <span
                onClick={() => handleRoaster(row)}
                className="btn-primary px-2 py-2 mr-2 rounded-lg"
              >
                <FaRegHospital />
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const exportColumns = [
    ...tableColumns,
    // { key: "profileDetails.accountType", label: "Account Type" },
    // { key: "profileDetails.branchName", label: "Branch Name" },
    // { key: "profileDetails.bankName", label: "Bank Name" },
    // { key: "profileDetails.accountNumber", label: "Account Number" },
    // { key: "profileDetails.accountHolderName", label: "Account Holder Name" },
    // { key: "profileDetails.ifscCode", label: "IFSC Code" },
  ];
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleView = (row: any): void => {
    console.log("row", row);
    const index = data.findIndex((items) => items.id === row.id);
    console.log("index", index);
    setCurrentIndex(index);
    setViewData(row);
    setIsView(true);
    <Link to={`/employee/${row.id}`} />;
  };
  const handleNavigation = (index: number) => {
    setCurrentIndex(index);
    setViewData(data[index]);
  };
  const handleRoaster = (row: any): void => {
    console.log("row", row);
    setSelectedEmployee(row as EmployeeData);
    setIsRosterAssigned(true);
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleEdit = (row: any): void => {
    setViewData(row);
    setEditEmployee(true);
  };


  const handleEditEmployeeClose = () => {
    setEditEmployee(false);
  };
  const addEditEmployeeClose = () => {
    setAddEmployee(false);
    setRefresh((prevKey) => prevKey + 1);
  };

  const handleSuccess = (message: string) => {
    setMessage(message);
    setSuccessToaster(true);
    setRefresh((prevKey) => prevKey + 1);
  };

  const handleError = (message: string) => {
    setMessage(message);
    setErrorToaster(true);
  };

  const closeToaster = () => {
    setErrorToaster(false);
    setSuccessToaster(false);
  };

  const userStatusCancel = () => {
    setChangeUserStatus(false);
    setRefresh((prevKey) => prevKey + 1);
  };

  // Parse the deactivation-preview response into assets/projects. A string
  // response (Case 2) yields empty lists → "No projects or assets found".
  const deactivationInfo =
    isResponse && typeof isResponse === "object" ? isResponse : null;
  const deactivationAssets = deactivationInfo?.assetModels ?? [];
  const deactivationProjects = deactivationInfo?.projectNames ?? [];
  const hasAssetsOrProjects =
    deactivationAssets.length > 0 || deactivationProjects.length > 0;
  // Name for the deactivation popups (falls back to a neutral phrase).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deactivationName = (currentStatusValue as any)?.displayName as string | undefined;

  // Handle change in items per page
  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage((prev) =>
      prev === newItemsPerPage ? prev : newItemsPerPage,
    );
    setCurrentPage(0);
  }, []);

  const handleServerPageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage - 1);
  }, []);

  const handleSearchChange = useCallback((newSearchTerm: string) => {
    setSearchQuery((prev) =>
      prev === newSearchTerm ? prev : newSearchTerm,
    );
    setCurrentPage(0);
  }, []);

  // Status options map directly onto the API `status` query param. The casing is
  // intentional ("ONBOARDING" is upper-case on the backend) — do not normalise it.
  const DEFAULT_STATUS = "active";
  const statusFilterOptions = [
    { value: "active", label: `${t("Active")} ${t("Employees")}` },
    { value: "inactive", label: `${t("Inactive")} ${t("Employees")}` },
    { value: "ONBOARDING", label: `${t("Onboarding")} ${t("Employees")}` },
  ];

  // Reuse the shared CommonListingFilter (same component/fields/buttons as Leaves).
  const employeeFilterFields: CommonFilterField[] = [
    {
      id: "employee-status",
      type: "native-select",
      label: "Status",
      widthClass: "min-w-[180px]",
      queryParam: "status",
      value: draftStatus,
      // Highlight the filter and enable Clear/Reset only when a non-default
      // status is applied.
      isActive: selectedStatus !== DEFAULT_STATUS,
      options: statusFilterOptions,
      onChange: (value) => setDraftStatus(value),
    },
  ];

  const employeeFilterControls = (
    <CommonListingFilter
      className="employee-listing-filter"
      fields={employeeFilterFields}
      panelTitle="Employee Filter"
      onApply={() => {
        setSelectedStatus(draftStatus);
        setCurrentPage(0);
      }}
      onClear={() => {
        setDraftStatus(DEFAULT_STATUS);
      }}
      onReset={() => {
        setDraftStatus(DEFAULT_STATUS);
        setSelectedStatus(DEFAULT_STATUS);
        setCurrentPage(0);
      }}
    />
  );

  return (
    <>
      <div className="absolute invisible pointer-events-none whitespace-nowrap">
        <span ref={nameReferenceRef} className="text-base font-semibold">
          admin liri
        </span>
        <span ref={emailReferenceRef} className="text-base font-semibold ml-6">
          admin@lirisoft.com
        </span>
      </div>

      {/* <BreadCrumb BreadCrumbList={BreadCrumbList} /> */}

      {data && !isView ? (
        <DataTable
          heightClass={dynamicHeightClass}
          data={data}
          columns={tableColumns}
          loading={loading}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage + 1}
          totalItems={totalItems}
          serverSidePagination={true}
          isExportable={false}
          onPageChange={handleServerPageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onSearchTermChange={handleSearchChange}
          searchDebounceMs={500}
          searchWidth="lg:w-48 xl:w-64"
          searchAutoComplete="off"
          // onAddBtnClick={() => setAddEmployee(true)}
          leftSideButtons={employeeFilterControls}
          customButton={
            <Export
              config={{
                data,
                columns: exportColumns,
                // Filename reflects the applied status filter, e.g. active_employees.
                filename: `${selectedStatus.toLowerCase()}_employees`,
              }}
            />
          }
        />
      ) : (
        viewData && (
          <EmployeeDetails
            data={viewData as any}
            allEmployees={data}
            onBack={() => setIsView(false)}
            onNavigate={handleNavigation}
            currentIndex={currentIndex}
            // Refetch the list so the table shows the edited values on back.
            onEmployeeUpdated={() => setRefresh((prev) => prev + 1)}
          />
        )
      )}

      {isSettingsOpen && (
        <Modal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          header="Tracking Settings"
          customWidth="max-w-7xl"
        >
          <DeviceSettings />
        </Modal>
      )}
      {changeUserStatus && (
        <Modal
          isOpen={changeUserStatus}
          onClose={() => setChangeUserStatus(false)}
          header="Change User Status"
        >
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="p-4 md:p-5 text-center">
              <svg
                className="mx-auto mb-4 text-gray-400 w-12 h-12 dark:text-gray-200"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              <h3 className="mb-5 text-lg font-normal text-slate-500 dark:text-slate-400">
                Are you sure you want to Change User Status?
              </h3>
              <button
                data-modal-hide="popup-modal"
                type="button"
                disabled={isStatusPreviewLoading}
                style={{ backgroundColor: colors.error }}
                className="text-white hover:opacity-90 focus:ring-4 focus:outline-none shadow-lg font-medium rounded-xl text-sm inline-flex items-center px-5 py-2.5 text-center transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={async () => {
                  // Preview first (silent), then reveal the result popup once it
                  // resolves — avoids opening the popup before data is available
                  // and avoids any notification before the user confirms.
                  setIsStatusPreviewLoading(true);
                  const data = await userStatusteHandle("api/user");
                  setIsStatusPreviewLoading(false);
                  setChangeUserStatus(false);
                  if (data !== null) setIsCollectAssets(true);
                }}
              >
                {isStatusPreviewLoading ? "Processing…" : "Yes, I'm sure"}
              </button>
              <button
                data-modal-hide="popup-modal"
                type="button"
                className="py-2.5 px-5 ms-3 text-sm font-medium text-slate-900 focus:outline-none bg-white rounded-xl border border-slate-200 hover:bg-slate-50 hover:text-primary focus:z-10 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600 dark:hover:text-white dark:hover:bg-slate-700 transition-all active:scale-95"
                onClick={userStatusCancel}
              >
                No, cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {isCollectAssets && (
        <Modal
          isOpen={isCollectAssets}
          onClose={() => setIsCollectAssets(false)}
          header={hasAssetsOrProjects ? "Confirm Deactivation" : "Deactivate Employee"}
        >
          <div className="text-slate-900 dark:text-white">
            {hasAssetsOrProjects ? (
              // ── Case 1: the user has assets and/or projects to hand over ──
              <>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                  Before deactivating{" "}
                  <span className="font-semibold">
                    {deactivationName || "this employee"}
                  </span>
                  , please collect the assigned assets. The employee will also be
                  removed from the projects listed below.
                </p>

                {deactivationAssets.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                      Assets to collect ({deactivationAssets.length})
                    </h4>
                    <ul className="flex flex-wrap gap-2">
                      {deactivationAssets.map((asset, i) => (
                        <li
                          key={`asset-${asset}-${i}`}
                          className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20 text-sm font-medium"
                        >
                          {asset}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {deactivationProjects.length > 0 && (
                  <div className="mb-1">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                      Removed from projects ({deactivationProjects.length})
                    </h4>
                    <ul className="flex flex-wrap gap-2">
                      {deactivationProjects.map((project, i) => (
                        <li
                          key={`project-${project}-${i}`}
                          className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-sm font-medium"
                        >
                          {project}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              // ── Case 2: nothing assigned — just confirm deactivation ──
              <div className="text-center py-2">
                <svg
                  className="mx-auto mb-3 text-gray-400 dark:text-gray-300 w-12 h-12"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                <p className="text-base font-medium text-slate-700 dark:text-slate-200 mb-1">
                  No projects or assets found for the user.
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Are you sure you want to mark{" "}
                  <span className="font-semibold">
                    {deactivationName || "this employee"}
                  </span>{" "}
                  as inactive?
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="py-2.5 px-5 text-sm font-medium text-slate-900 focus:outline-none bg-white rounded-xl border border-slate-200 hover:bg-slate-50 hover:text-primary focus:z-10 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600 dark:hover:text-white dark:hover:bg-slate-700 transition-all active:scale-95"
                onClick={() => setIsCollectAssets(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ backgroundColor: colors.error }}
                className="text-white hover:opacity-90 focus:ring-4 focus:outline-none shadow-lg font-medium rounded-xl text-sm inline-flex items-center px-5 py-2.5 text-center transition-all active:scale-95"
                onClick={() => {
                  userShouldTrue(`api/user?shouldRemoveUser=true`);
                  setIsCollectAssets(false);
                }}
              >
                {hasAssetsOrProjects ? "Confirm & Deactivate" : "Yes, Deactivate"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {isClickProject && projects.length > 0 && (
        <Modal
          isOpen={isClickProject}
          onClose={() => { setIsClickProject(false), setProjects([]) }}
          header="Assigned Projects"
        >
          <div className="p-4 sm:p-5">
            {/* Count summary — gives context on how many projects are assigned */}
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {projects.length} project{projects.length === 1 ? "" : "s"} assigned to this employee
            </p>

            {/* Responsive, aligned card grid. `gap` (not space-y/margins) keeps
                every card evenly spaced; the container scrolls instead of the
                old `overflow-hidden`, which silently clipped extra projects. */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {projects.map((project) => (
                <li
                  key={project.projectId}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all"
                >
                  {/* Initial avatar for quick visual scanning */}
                  <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-semibold uppercase">
                    {project.name?.charAt(0) || "#"}
                  </span>
                  {/* Truncates on one line; shows a portal tooltip on hover
                      only when the name is actually clipped. */}
                  <ProjectName name={project.name} />
                </li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      {addEmployee && (
        <Modal
          isOpen={addEmployee}
          onClose={addEditEmployeeClose}
          header="Add Employee"
        >
          <AddEmployee onClose={addEditEmployeeClose} />
        </Modal>
      )}
      {editEmployee && (
        <Modal
          isOpen={editEmployee}
          onClose={() => setEditEmployee(false)}
          header="Edit Employee"
          // Wider modal so the 3-column form fits without horizontal cramping or
          // vertical scrolling (capped to the viewport on smaller screens).
          customWidth="min(880px, 95vw)"
        >
          <EditEmployee
            onClose={handleEditEmployeeClose}
            data={viewData}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        </Modal>
      )}
      {isRosterAssigned && (
        <EmployeeRosterForm
          isOpen={isRosterAssigned}
          onClose={() => {
            setIsRosterAssigned(false);
            setSelectedEmployee(null);
          }}
          onSave={async (payload: any, _id?: number, isEdit = false) => {
            try {
              console.log("Saving roster data:", payload);

              // Mapping from abbreviated to full day names
              const dayMapping: { [key: string]: string } = {
                Mon: "Monday",
                Tue: "Tuesday",
                Wed: "Wednesday",
                Thu: "Thursday",
                Fri: "Friday",
                Sat: "Saturday",
                Sun: "Sunday",
              };

              // Transform availability days to full names
              const transformedPayload = {
                ...payload,
                availability: payload.availability.map(
                  (day: string) => dayMapping[day] || day,
                ),
              };

              const response = isEdit
                ? await apiService.put(
                  `/api/roster-data/update/${_id}`,
                  transformedPayload,
                )
                : await apiService.post(
                  "api/roster-data/save",
                  transformedPayload,
                );

              if (response.status === 200 || response.status === 201) {
                handleSuccess(
                  isEdit
                    ? "Agent updated successfully in roster!"
                    : "Agent saved successfully in roster!",
                );
                setRefresh((prevKey) => prevKey + 1);
                setIsRosterAssigned(false);
                setSelectedEmployee(null);
              }
            } catch (error: any) {
              console.error("Error saving roster:", error);
              handleError(
                error?.response?.data?.message ||
                "Failed to save employee roster. Please try again.",
              );
            }
          }}
          agent={selectedEmployee}
        />
      )}

      {(showSuccessToaster || showErrorToaster) && (
        <GenericToaster
          message={message}
          onClose={closeToaster}
          open={showSuccessToaster || showErrorToaster}
          severity={showErrorToaster ? "error" : "info"}
        />
      )}
    </>
  );
}
export default EmployeeList;
